import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { cn } from "@/lib/utils";

export type CarouselStat = {
  key: string;
  label: string;
  value: string;
  accent?: boolean;
  hint?: string;
};

interface Props {
  items: CarouselStat[];
  ariaLabel?: string;
}

export default function MobileStatCarousel({ items, ariaLabel = "Today's headline stats" }: Props) {
  const [emblaRef, emblaApi] = useEmblaCarousel({ align: "start", containScroll: "trimSnaps", dragFree: false });
  const [selected, setSelected] = useState(0);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelected(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
  }, [emblaApi, onSelect]);

  return (
    <div role="region" aria-label={ariaLabel} aria-roledescription="carousel">
      <div className="overflow-hidden -mx-4 px-4" ref={emblaRef}>
        <div className="flex gap-3">
          {items.map((s, i) => (
            <div
              key={s.key}
              role="group"
              aria-roledescription="slide"
              aria-label={`${i + 1} of ${items.length}: ${s.label}`}
              className="basis-[78%] shrink-0 snap-start"
            >
              <div className="rounded-xl border border-border/70 bg-secondary/25 px-4 py-3 h-full">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{s.label}</div>
                <div
                  className={cn(
                    "font-display text-2xl font-bold mt-1 tabular-nums leading-tight",
                    s.accent && "te-grad-text",
                  )}
                >
                  {s.value}
                </div>
                {s.hint && <div className="text-[11px] text-muted-foreground/80 mt-1 truncate">{s.hint}</div>}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="mt-2.5 flex items-center justify-center gap-1.5" role="tablist" aria-label="Stat indicators">
        {items.map((s, i) => (
          <button
            key={s.key}
            type="button"
            role="tab"
            aria-selected={i === selected}
            aria-label={`Show ${s.label}`}
            onClick={() => emblaApi?.scrollTo(i)}
            className={cn(
              "h-1.5 rounded-full transition-all",
              i === selected ? "w-6 bg-primary" : "w-1.5 bg-border hover:bg-muted-foreground/40",
            )}
          />
        ))}
      </div>
    </div>
  );
}
